import { useState, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { ApiService } from "../../api/services.gen";
import { useParams, useNavigate } from "react-router";
import { useAlert } from "../../constants/AlertProvider";
import RecHomeNavbar from "./RecHomeNavbar";
import { PlusIcon, TargetIcon, BarChartIcon, Search, X, PencilIcon, Trash2Icon } from "lucide-react";
import { SkeletonLoader } from "../common/SkeletonLoader";

const LEVEL_OPTIONS = {
    'Beginner': 2,
    'Intermediate': 4,
    'Advanced': 6,
    'Expert': 8,
    'Master': 10
};

const WEIGHT_OPTIONS = {
    'Nice to have': 0.2,
    'Helpful': 0.4,
    'Important': 0.6,
    'Very Important': 0.8,
    'Mandatory': 1.0
};

const findOptionLabel = (options, value) => {
    const normalizedValue = Number(value); 
    return Object.keys(options).find((key) => Number(options[key]) === normalizedValue) || value;
};

export default function SkillPost() {
    const { id: jobId } = useParams();
    const navigate = useNavigate();
    const showAlert = useAlert();

    const [matrixId, setMatrixId] = useState(null);
    const [addedSkills, setAddedSkills] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isAddingSkill, setIsAddingSkill] = useState(false);

    const [query, setQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [selectedSkill, setSelectedSkill] = useState(null);
    const [requiredLevelName, setRequiredLevelName] = useState('Intermediate');
    const [weightLevelName, setWeightLevelName] = useState('Important');

    useEffect(() => {
        let ignore = false;

        const fetchInitialData = async () => {
            try {
                const [skillsRes, matrixRes] = await Promise.all([
                    ApiService.apiMatchingSkillsList({ limit: 1000 }),
                    ApiService.apiJobSkillMatricesList({ limit: 1000 }),
                ]);

                if (ignore) {
                    return;
                }
 
                const skillsList = skillsRes?.results || (Array.isArray(skillsRes) ? skillsRes : []);
                const skillsById = new Map(skillsList.map((skill) => [skill.id, skill]));

                const matrixList = matrixRes?.results || (Array.isArray(matrixRes) ? matrixRes : []);

                // Find matrix for the current job
                const currentMatrix = matrixList.find(m => m.job === Number(jobId));

                if (currentMatrix) {
                    setMatrixId(currentMatrix.id);
                    if (currentMatrix.skills_data) {
                        const existingSkills = Object.entries(currentMatrix.skills_data).map(([skillId, data]) => {
                            const skillObj = skillsById.get(Number(skillId));
                            const levelName = findOptionLabel(LEVEL_OPTIONS, data.required_level);
                            const weightName = findOptionLabel(WEIGHT_OPTIONS, data.weight);
                            return {
                                skill_id: Number(skillId),
                                name: skillObj ? skillObj.name : "Unknown Skill",
                                required_level: data.required_level,
                                weight: data.weight,
                                levelName,
                                weightName
                            };
                        });
                        setAddedSkills(existingSkills);
                    }
                } else {
                    showAlert("Could not find a skill matrix for this job.", "error");
                }
            } catch (error) {
                if (!ignore) {
                    console.error("Error fetching data:", error);
                    showAlert("Failed to load skills data.", "error");
                }
            } finally {
                if (!ignore) {
                    setIsLoading(false);
                }
            }
        };

        if (jobId) {
            fetchInitialData();
        } else {
            setIsLoading(false);
        }

        return () => {
            ignore = true;
        };
    }, [jobId, showAlert]);

    // Autocomplete effect
    useEffect(() => {
        if (selectedSkill && query === selectedSkill.name) {
            setSuggestions([]);
            return;
        }

        const controller = new AbortController();

        const fetchSkills = async () => {
            const trimmedQuery = query.trim();

            if (trimmedQuery.length < 2) {
                setSuggestions([]);
                return;
            }

            try {
                const res = await axios.get(
                    `/api/matching-skills/autocomplete/?q=${encodeURIComponent(trimmedQuery)}`,
                    { signal: controller.signal }
                );
                setSuggestions(res.data.skills || []);
            } catch (err) {
                if (!axios.isCancel(err) && err.name !== "CanceledError") {
                    console.error("Error fetching skills:", err);
                }
            }
        };

        const delayDebounce = setTimeout(fetchSkills, 300);

        return () => {
            controller.abort();
            clearTimeout(delayDebounce);
        };
    }, [query, selectedSkill]);

    const handleSelectSkill = (skill) => {
        setSelectedSkill(skill);
        setQuery(skill.name);
        setSuggestions([]);
    };

    const handleAddSkill = async () => {
        if (!matrixId) {
            showAlert("No skill matrix was found for this job.", "error");
            return;
        }

        if (!selectedSkill) {
            showAlert("Please select a skill from the search results.", "error");
            return;
        }

        const isUpdating = addedSkills.some((s) => s.skill_id === selectedSkill.id);
        const requiredLevel = LEVEL_OPTIONS[requiredLevelName];
        const weight = WEIGHT_OPTIONS[weightLevelName];

        setIsAddingSkill(true);
        try {
            const token = Cookies.get("csrftoken");
            await axios.post(`/api/job-skill-matrices/${matrixId}/update_skill/`, {
                skill_id: selectedSkill.id,
                required_level: requiredLevel,
                weight: weight
            }, {
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": token || ""
                },
                withCredentials: true
            });

            setAddedSkills(prev => {
                const existingIndex = prev.findIndex((s) => s.skill_id === selectedSkill.id);
                const newSkill = {
                    skill_id: selectedSkill.id,
                    name: selectedSkill.name,
                    required_level: requiredLevel,
                    weight: weight,
                    levelName: requiredLevelName,
                    weightName: weightLevelName
                };

                if (existingIndex >= 0) {
                    const newSkills = [...prev];
                    newSkills[existingIndex] = newSkill;
                    return newSkills;
                }
                return [...prev, newSkill];
            });

            showAlert(isUpdating ? "Skill updated successfully!" : "Skill added successfully!", "success");
            setSelectedSkill(null);
            setQuery("");
            setRequiredLevelName('Intermediate');
            setWeightLevelName('Important');

        } catch (error) {
            console.error("Error adding skill:", error);
            showAlert("Failed to add skill.", "error");
        } finally {
            setIsAddingSkill(false);
        }
    };

    const handleEditSkill = (skill) => {
        setSelectedSkill({ id: skill.skill_id, name: skill.name });
        setQuery(skill.name);
        setSuggestions([]);
        setRequiredLevelName(skill.levelName || 'Intermediate');
        setWeightLevelName(skill.weightName || 'Important');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDeleteSkill = async (skillIdToDelete) => {
        if (!matrixId) return;
        if (!window.confirm("Are you sure you want to remove this skill?")) return;

        try {
            const newSkillsList = addedSkills.filter(s => s.skill_id !== skillIdToDelete);
            
            // Reconstruct skills_data to bulk update the matrix without the deleted skill
            const skills_data = {};
            newSkillsList.forEach(s => {
                skills_data[s.skill_id] = {
                    required_level: s.required_level,
                    weight: s.weight
                };
            });

            const token = Cookies.get("csrftoken");
            await axios.patch(`/api/job-skill-matrices/${matrixId}/`, {
                skills_data: skills_data
            }, {
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": token || ""
                },
                withCredentials: true
            });

            setAddedSkills(newSkillsList);
            showAlert("Skill removed successfully!", "success");
            
            if (selectedSkill?.id === skillIdToDelete) {
                setSelectedSkill(null);
                setQuery("");
            }
        } catch (error) {
            console.error("Error removing skill:", error);
            showAlert("Failed to remove skill.", "error");
        }
    };

    const handleFinish = () => {
        navigate("/myjobs");
        showAlert("Job posting completed!", "success");
    };

    return (
        <>
            <RecHomeNavbar />
            <div className="mt-14 min-h-screen bg-gray-50 dark:bg-gray-900 w-full">
                <header className="w-full mx-auto px-4 py-8 sm:px-6 lg:px-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
                        <TargetIcon className="mr-2 h-6 w-6 text-blue-600" />
                        Add Skills to Job
                    </h1>
                </header>

                <main className="w-full mx-auto px-4 pb-8 sm:px-6 lg:px-8">
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
                        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Required Skills & Expertise</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                            Search and add core skills required for this role. Specify the desired proficiency level and how important each skill is.
                        </p>

                        {/* Search Input Box */}
                        <div className="w-full md:w-3/5 mb-6">
                            <label htmlFor="skill-search" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Search Skills
                            </label>
                            <div className="w-4/5 relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                                <input
                                    id="skill-search"
                                    type="text"
                                    value={query}
                                    onChange={(e) => {
                                        setQuery(e.target.value);
                                        if (selectedSkill && e.target.value !== selectedSkill.name) {
                                            setSelectedSkill(null);
                                        }
                                    }}
                                    className="w-full pl-10 pr-10 h-10 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 transition-colors"
                                    placeholder="Search... (e.g. React, Python)"
                                    disabled={isLoading}
                                    autoComplete="off"
                                />
                                {query && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setQuery("");
                                            setSelectedSkill(null);
                                            setSuggestions([]);
                                        }}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                )}

                                {suggestions.length > 0 && !selectedSkill && (
                                    <ul className="absolute top-full mt-2 w-full border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-xl max-h-60 overflow-y-auto z-20">
                                        {suggestions.map((skill) => (
                                            <li
                                                key={skill.id || skill.name}
                                                onClick={() => handleSelectSkill(skill)}
                                                className="p-3 hover:bg-blue-50 dark:hover:bg-blue-900/30 cursor-pointer text-sm text-gray-700 dark:text-gray-300 font-medium border-b border-gray-100 dark:border-gray-700 last:border-0 transition-colors"
                                            >
                                                {skill.name}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>

                        {/* Level and Weight Configuration */}
                        {selectedSkill && (
                            <div className="bg-gray-50 dark:bg-gray-900 p-5 rounded-xl border border-gray-200 dark:border-gray-700 animate-in fade-in slide-in-from-top-4 duration-300">
                                <div className="mb-6 flex items-center justify-between">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                            Configure requirements for <span className="text-blue-600">"{selectedSkill.name}"</span>
                                        </h3>
                                    </div>
                                </div>

                                <div className="space-y-8">
                                    {/* Required Level Selection */}
                                    <div>
                                        <p className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
                                            Required Proficiency Level
                                        </p>
                                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                            {Object.keys(LEVEL_OPTIONS).map((levelName) => (
                                                <label
                                                    key={levelName}
                                                    className={`flex flex-col items-center justify-center rounded-lg border-2 p-3 cursor-pointer transition-all
                                                    ${requiredLevelName === levelName
                                                            ? "border-blue-600 bg-blue-50/50 shadow-sm"
                                                            : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                                        }`}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="requiredLevel"
                                                        value={levelName}
                                                        checked={requiredLevelName === levelName}
                                                        onChange={() => setRequiredLevelName(levelName)}
                                                        className="sr-only"
                                                    />
                                                    <span className={`text-sm font-semibold ${requiredLevelName === levelName ? "text-blue-700 dark:text-blue-400" : "text-gray-600 dark:text-gray-400"}`}>
                                                        {levelName}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Weight / Importance Selection */}
                                    <div>
                                        <p className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
                                            How important is this skill for the job?
                                        </p>
                                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                            {Object.keys(WEIGHT_OPTIONS).map((wName) => (
                                                <label
                                                    key={wName}
                                                    className={`flex flex-col items-center text-center justify-center rounded-lg border-2 p-3 cursor-pointer transition-all
                                                    ${weightLevelName === wName
                                                            ? "border-green-600 bg-green-50/50 shadow-sm"
                                                            : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-green-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                                        }`}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="weightLevel"
                                                        value={wName}
                                                        checked={weightLevelName === wName}
                                                        onChange={() => setWeightLevelName(wName)}
                                                        className="sr-only"
                                                    />
                                                    <span className={`text-sm font-semibold leading-tight ${weightLevelName === wName ? "text-green-700 dark:text-green-400" : "text-gray-600 dark:text-gray-400"}`}>
                                                        {wName}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="flex justify-end pt-2">
                                        <button
                                            type="button"
                                            onClick={handleAddSkill}
                                            disabled={isLoading || isAddingSkill}
                                            className="inline-flex justify-center items-center py-2.5 px-6 min-w-[140px] border border-transparent shadow-sm text-sm font-bold rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-70 transition-all"
                                        >
                                            {isAddingSkill ? (
                                                <div className="flex items-center justify-center space-x-1.5 h-5">
                                                    <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                                    <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                                    <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                                </div>
                                            ) : (
                                                <>
                                                    <PlusIcon className="mr-2 h-5 w-5" />
                                                    {addedSkills.some(s => s.skill_id === selectedSkill?.id) ? "Update Skill" : "Add Skill"}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {isLoading ? (
                        <div className="mb-6">
                            <SkeletonLoader />
                        </div>
                    ) : addedSkills.length > 0 && (
                        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden mb-6">
                            <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex items-center">
                                <BarChartIcon className="mr-2 h-5 w-5 text-gray-500" />
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                    Current Job Skills Matrix
                                </h3>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-white dark:bg-gray-800">
                                        <tr>
                                            <th scope="col" className="py-4 pl-6 pr-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Skill Name</th>
                                            <th scope="col" className="px-3 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Required Level</th>
                                            <th scope="col" className="px-3 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Importance</th>
                                            <th scope="col" className="px-3 py-4 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
                                        {addedSkills.map((skill) => (
                                            <tr key={skill.skill_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                                <td className="whitespace-nowrap py-4 pl-6 pr-3 text-sm font-medium text-gray-900 dark:text-gray-100">{skill.name}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600 dark:text-gray-400">
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300">
                                                        {skill.levelName || 'Unknown'}
                                                    </span>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600 dark:text-gray-400">
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300">
                                                        {skill.weightName || 'Unknown'}
                                                    </span>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-right text-sm font-medium">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleEditSkill(skill)}
                                                        className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 mr-4"
                                                        title="Edit Skill"
                                                    >
                                                        <PencilIcon className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteSkill(skill.skill_id)}
                                                        className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                                                        title="Remove Skill"
                                                    >
                                                        <Trash2Icon className="h-4 w-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end mt-8">
                        <button
                            type="button"
                            onClick={handleFinish}
                            className="inline-flex justify-center items-center py-2 px-8 border border-transparent shadow-md text-base font-semibold rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none transition-all"
                        >
                            Complete Job Posting
                        </button>
                    </div>
                </main >
            </div >
        </>
    );
}
